import React, { useEffect, useMemo, useState } from 'react';
import { Search, Plus, X, GripVertical } from 'lucide-react';
import { CatalogAPI, AdminAPI, resolveImage, formatPrice } from '../../lib/api';
import { Input } from '../ui/input';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/**
 * Picker for "Featured products" on the homepage.
 * - Value: array of product IDs (stored in site_config['home.featured_product_ids'])
 * - Lets admin search the catalog and add/remove products, reorder by up/down.
 */
const SortableSelected = ({ p, idx, total, move, remove, lowestPrice }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: p.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-3 p-2 bg-[#FAF7F2] rounded-lg border border-[#E8E0D0]">
      <div className="flex flex-col gap-0.5">
        <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-[#7A6F5C] hover:text-[#1F4D3F] disabled:opacity-30" title="Monter">▲</button>
        <button onClick={() => move(idx, 1)} disabled={idx === total - 1} className="text-[#7A6F5C] hover:text-[#1F4D3F] disabled:opacity-30" title="Descendre">▼</button>
      </div>
      <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing touch-none text-[#7A6F5C]" title="Glisser pour deplacer">
        <GripVertical className="w-4 h-4" />
      </button>
      {p.image ? (
        <img src={resolveImage(p.image)} alt="" className="w-10 h-10 rounded object-cover" onError={e => { e.target.style.visibility = 'hidden'; }} />
      ) : (
        <div className="w-10 h-10 rounded bg-[#E8E0D0]" />
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-[#2D2A26] truncate">{p.name}</div>
        <div className="text-xs text-[#7A6F5C]">{p.brand} {lowestPrice(p) ? `· dès ${formatPrice(lowestPrice(p))}` : ''}</div>
      </div>
      <button onClick={() => remove(p.id)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600" title="Retirer">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

const FeaturedProductsPicker = ({ value = [], onChange }) => {
  const ids = Array.isArray(value) ? value : [];
  const [allProducts, setAllProducts] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }));

  useEffect(() => {
    AdminAPI.products({ limit: 500 }).then((data) => {
      setAllProducts(Array.isArray(data) ? data : []);
    }).catch(() => setAllProducts([])).finally(() => setLoading(false));
  }, []);

  const byId = useMemo(() => Object.fromEntries(allProducts.map(p => [p.id, p])), [allProducts]);
  const selected = ids.map(id => byId[id]).filter(Boolean);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return allProducts.filter(p => !ids.includes(p.id))
      .filter(p => !term || (p.name || '').toLowerCase().includes(term) || (p.brand || '').toLowerCase().includes(term))
      .slice(0, 12);
  }, [allProducts, q, ids]);

  const add = (id) => onChange([...ids, id]);
  const remove = (id) => onChange(ids.filter(x => x !== id));
  const move = (idx, dir) => {
    const next = [...ids];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  const lowestPrice = (p) => {
    if (!p.variants?.length) return null;
    return Math.min(...p.variants.map(v => v.price || Infinity).filter(Number.isFinite));
  };

  return (
    <div className="bg-white border border-[#E8E0D0] rounded-2xl p-5" data-testid="featured-products-picker">
      <p className="text-sm text-[#7A6F5C] mb-4">
        Sélectionnez les produits à mettre en avant sur la page d'accueil. Ils s'affichent dans une section dédiée juste sous le bandeau principal.
        Recommandé : 4 à 8 produits. Glisser pour réordonner via les flèches.
      </p>

      <div className="mb-4">
        <div className="text-xs uppercase tracking-wider text-[#1F4D3F] mb-2">
          Produits sélectionnés <span className="text-[#7A6F5C] normal-case ml-1">({selected.length})</span>
        </div>
        {selected.length === 0 ? (
          <div className="text-center text-[#7A6F5C] py-4 text-sm border border-dashed border-[#E8E0D0] rounded-lg">
            Aucun produit sélectionné. Cliquez sur un produit ci-dessous pour l'ajouter.
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter}
            onDragEnd={({ active, over }) => {
              if (!over || active.id === over.id) return;
              const oldIndex = ids.indexOf(active.id);
              const newIndex = ids.indexOf(over.id);
              if (oldIndex < 0 || newIndex < 0) return;
              onChange(arrayMove(ids, oldIndex, newIndex));
            }}>
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {selected.map((p, idx) => (
                  <SortableSelected key={p.id} p={p} idx={idx} total={selected.length} move={move} remove={remove} lowestPrice={lowestPrice} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      <div className="border-t border-[#E8E0D0] pt-4">
        <div className="text-xs uppercase tracking-wider text-[#1F4D3F] mb-2">Ajouter un produit</div>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A6F5C]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher par nom ou marque..." className="pl-9" data-testid="featured-search" />
        </div>
        {loading ? (
          <div className="text-center text-[#7A6F5C] py-4 text-sm">Chargement du catalogue…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-[#7A6F5C] py-4 text-sm">Aucun produit trouvé.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto">
            {filtered.map(p => (
              <button key={p.id} type="button" onClick={() => add(p.id)}
                data-testid={`featured-add-${p.id}`}
                className="flex items-center gap-2 p-2 border border-[#E8E0D0] rounded-lg hover:border-[#1F4D3F] hover:bg-[#FAF7F2] text-left transition">
                {p.image ? (
                  <img src={resolveImage(p.image)} alt="" className="w-8 h-8 rounded object-cover" onError={e => { e.target.style.visibility = 'hidden'; }} />
                ) : (
                  <div className="w-8 h-8 rounded bg-[#E8E0D0]" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-[#2D2A26] truncate">{p.name}</div>
                  <div className="text-[10px] text-[#7A6F5C] truncate">{p.brand}</div>
                </div>
                <Plus className="w-4 h-4 text-[#1F4D3F] shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeaturedProductsPicker;
